// Tiny fetch wrapper for the PHP REST API at /api.
//
// In production the site and API share the domain (visibleonline.in), so the
// default base "/api" resolves same-origin. For local development the CRA dev
// server proxies /api to https://visibleonline.in/api/ (see src/setupProxy.js).
// API_PROXY_TARGET can override the backend origin for local development.

const BASE = process.env.REACT_APP_API_BASE_URL || "/api";
const TOKEN_KEY = "vo_admin_token";

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore storage errors (private mode, etc.) */
  }
}

export function clearToken() {
  setToken(null);
}

// An Error subclass carrying the API's machine-readable `code` so callers
// (e.g. Admin's friendlyError) can map it to a friendly message.
export class ApiError extends Error {
  constructor(code, message) {
    super(message || code);
    this.code = code;
  }
}

async function request(method, path, { body, auth = false } = {}) {
  const headers = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    // Network / connection failure (server down, offline, DNS, CORS).
    throw new ApiError("network", "Could not reach the server.");
  }

  if (res.status === 204) return null;

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const err = data && data.error ? data.error : {};
    throw new ApiError(err.code || `http_${res.status}`, err.message || "Request failed.");
  }

  return data;
}

export const api = {
  get:   (path, opts)        => request("GET",    path, opts),
  post:  (path, body, opts)  => request("POST",   path, { ...opts, body }),
  put:   (path, body, opts)  => request("PUT",    path, { ...opts, body }),
  patch: (path, body, opts)  => request("PATCH",  path, { ...opts, body }),
  del:   (path, opts)        => request("DELETE", path, opts),
};
